// Champion Duel — tracks a "CHALLENGE THEM" queued ghost race and rewards
// +2 life tokens if the player beats the ghost's score cleanly on the FIRST
// attempt. Life tokens are consumed by future runs (one per run).
//
// State:
//   ppe_duel_active_v1 = { handle, ghostScore, startedAt, attempts }
//   ppe_life_tokens_v1 = number  (persistent extra lives, one consumed per run)

const DUEL_KEY = "ppe_duel_active_v1";
const LIFE_KEY = "ppe_life_tokens_v1";
const STREAK_KEY = "ppe_duel_streak_v1";
const BONUS_KEY = "ppe_duel_3x_pending_v1";

/** Mark a duel as active for the next daily run. Called from fetchTopGhost
 *  when a challenge target was queued. */
export function armDuel(handle, ghostScore) {
  const doc = {
    handle: String(handle || "").slice(0, 40),
    ghostScore: Math.max(0, Math.floor(ghostScore || 0)),
    startedAt: Date.now(),
    attempts: 0,
  };
  localStorage.setItem(DUEL_KEY, JSON.stringify(doc));
  return doc;
}

export function getDuel() {
  try {
    const raw = localStorage.getItem(DUEL_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || typeof d.ghostScore !== "number") return null;
    return d;
  } catch {
    return null;
  }
}

/** Record a duel attempt (call once per daily run start). Returns updated attempts. */
export function tickDuelAttempt() {
  const d = getDuel();
  if (!d) return 0;
  d.attempts = (d.attempts || 0) + 1;
  localStorage.setItem(DUEL_KEY, JSON.stringify(d));
  return d.attempts;
}

/** Resolve a duel outcome. If this was the FIRST attempt AND the player's
 *  score > ghostScore AND they won cleanly (survived), award +2 life tokens
 *  and clear the duel. Returns { won, cleanFirstWin, awarded }. */
export function resolveDuel({ playerScore, won }) {
  const d = getDuel();
  if (!d) return { won: false, cleanFirstWin: false, awarded: 0 };
  const cleanFirstWin =
    !!won && (d.attempts || 0) <= 1 && playerScore > d.ghostScore;
  let awarded = 0;
  let newStreak = getDuelStreak();
  let duelistUnlocked = false;
  let bonusArmed = false;
  if (cleanFirstWin) {
    awarded = 2;
    setLifeTokens(getLifeTokens() + awarded);
    newStreak = newStreak + 1;
    setDuelStreak(newStreak);
    if (newStreak >= 3) {
      duelistUnlocked = true;
      armDuelistBonus(); // 3× chips on next daily run
      bonusArmed = true;
    }
  } else {
    // Any non-clean-first-win breaks the streak
    setDuelStreak(0);
    newStreak = 0;
  }
  // Always clear after resolving (win or loss ends the duel)
  localStorage.removeItem(DUEL_KEY);
  return {
    won: !!won,
    cleanFirstWin,
    awarded,
    handle: d.handle,
    ghostScore: d.ghostScore,
    streak: newStreak,
    duelistUnlocked,
    bonusArmed,
  };
}

export function clearDuel() {
  localStorage.removeItem(DUEL_KEY);
}

// ---- Life tokens (one-shot extra lives that carry across runs) ----

export function getLifeTokens() {
  const n = parseInt(localStorage.getItem(LIFE_KEY) || "0", 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function setLifeTokens(n) {
  const v = Math.max(0, Math.floor(n));
  if (v <= 0) localStorage.removeItem(LIFE_KEY);
  else localStorage.setItem(LIFE_KEY, String(v));
}

/** Consume 1 life token if available. Returns true if consumed. */
export function consumeLifeToken() {
  const cur = getLifeTokens();
  if (cur <= 0) return false;
  setLifeTokens(cur - 1);
  return true;
}

// ---- Duel streak & DUELIST title ----

export function getDuelStreak() {
  const n = parseInt(localStorage.getItem(STREAK_KEY) || "0", 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function setDuelStreak(n) {
  const v = Math.max(0, Math.floor(n));
  if (v <= 0) localStorage.removeItem(STREAK_KEY);
  else localStorage.setItem(STREAK_KEY, String(v));
}

/** True once the player has ever hit a 3+ duel streak. */
export function hasDuelistTitle() {
  return getDuelStreak() >= 3;
}

// ---- One-shot 3× chip bonus for the next daily after a 3+ streak ----

export function armDuelistBonus() {
  localStorage.setItem(BONUS_KEY, "1");
}

export function isDuelistBonusPending() {
  return localStorage.getItem(BONUS_KEY) === "1";
}

export function consumeDuelistBonus() {
  const on = isDuelistBonusPending();
  localStorage.removeItem(BONUS_KEY);
  return on;
}
